import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import { Alert, Box, Button, Skeleton, Stack, Tab, Tabs, Tooltip, useTheme } from '@mui/material';
import { useIntl } from 'react-intl';

import {
  ArtifactDetailHeader,
  Button as WaitButton,
  Icon,
  type ProjectDetailHeaderMetaItem,
} from '@verantos-dev/shared-components';
import { DatasetDetailsLink } from '@verantos-dev/ui-cohort-builder-components';

import { AdDownloadModal } from '../components/AdDownloadModal';
import { AdProcessModal } from '../components/AdProcessModal';
import { AdStagedChangesPill } from '../components/AdStagedChangesPill';
import { AnalyticalDatasetDefinitionTab } from '../containers/definition/AnalyticalDatasetDefinitionTab';
import { AnalyticalDatasetResultsTab } from '../containers/results/AnalyticalDatasetResultsTab';
import { useAdDownloadInFlight } from '../hooks/useAdDownloadInFlight';
import { useAnalyticalDatasetBreadcrumbs } from '../hooks/useAnalyticalDatasetBreadcrumbs';
import { useAnalyticalDatasetDetailMetaRow } from '../hooks/useAnalyticalDatasetDetailMetaRow';
import { useAnalyticalDatasetDetailPage } from '../hooks/useAnalyticalDatasetDetailPage';
import { toAssetStatus } from '../utils/assetStatusLabel';

// Type-caption icon for the condensed meta row. Sized to sit alongside the
// shared `Icon` glyphs (owner/calendar) which render at 16px.
const HEADER_ICON_SX = { fontSize: 18, color: 'text.secondary' } as const;

export const AnalyticalDatasetDetailPage = () => {
  const { formatMessage, formatDate, formatTime } = useIntl();
  const theme = useTheme();
  const {
    projectId,
    analyticalDatasetProfileId,
    pageTitle,
    isProfileLoading,
    activeTab,
    onTabChange,
    onProcess,
    isProcessModalOpen,
    onCloseProcessModal,
    isInlineProcessEnabled,
    isProcessSubmitting,
    processError,
    canProcess,
    processTooltip,
    onDownload,
    isDownloadModalOpen,
    onCloseDownloadModal,
    canDownload,
  } = useAnalyticalDatasetDetailPage();
  // OR-6123 F8: breadcrumb on top, title below — gated on gen_3 (legacy route
  // never had a project-header breadcrumb above this page).
  const breadcrumbs = useAnalyticalDatasetBreadcrumbs(pageTitle);
  // The export fetch survives the modal closing — while it runs in the background
  // the Download button stays disabled and explains itself via the tooltip.
  const isDownloadInFlight = useAdDownloadInFlight(projectId, analyticalDatasetProfileId);
  // Single source of truth for the Download button's disabled state so the icon
  // can adopt the disabled glyph alongside the label (the shared `Icon` hardcodes
  // its fill and won't otherwise grey out with the button).
  const isDownloadDisabled = isDownloadModalOpen || !canDownload || isDownloadInFlight;

  // Condensed header: status badge next to the title, and a Type · Owner ·
  // Last modified meta row — rendered by the shared ProjectDetailHeader so the
  // dataset header matches the other project detail pages.
  const {
    isReady: isMetaReady,
    status,
    ownerName,
    lastModifiedDate,
    datasetReleaseId,
    datasetReleaseName,
  } = useAnalyticalDatasetDetailMetaRow();

  // Unified asset-status vocabulary (Draft / Processing / Ready / Error) in
  // the status badge.
  const effectiveStatus = status ? toAssetStatus(status) : status;
  // Mirror the cohort/AV header's `date | time` format.
  const lastModified = lastModifiedDate
    ? `${formatDate(lastModifiedDate, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })} | ${formatTime(lastModifiedDate, { hour: 'numeric', minute: '2-digit' })}`
    : null;

  const metaItems = (
    [
      {
        key: 'type',
        icon: <TableChartOutlinedIcon sx={HEADER_ICON_SX} />,
        label: formatMessage({ id: 'analyticalDatasetDetail.meta.typeLabel' }),
        value: formatMessage({ id: 'analyticalDatasetDetail.meta.typeValue' }),
      },
      ownerName
        ? {
            key: 'owner',
            icon: (
              <Icon
                id="ad-detail-owner-icon"
                icon="owner"
                width={16}
                height={16}
                sx={{ fill: theme.palette.text.secondary }}
              />
            ),
            label: formatMessage({ id: 'analyticalDatasetDetail.meta.ownerLabel' }),
            value: ownerName,
          }
        : null,
      lastModified
        ? {
            key: 'lastModified',
            icon: (
              <AccessTimeOutlinedIcon
                data-testid="ad-detail-last-modified-icon"
                sx={HEADER_ICON_SX}
              />
            ),
            label: formatMessage({ id: 'analyticalDatasetDetail.meta.lastModifiedLabel' }),
            value: lastModified,
          }
        : null,
    ] as (ProjectDetailHeaderMetaItem | null)[]
  ).filter((item): item is ProjectDetailHeaderMetaItem => item !== null);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>
      {isProfileLoading ? (
        // Matches the header's vertical rhythm so the swap doesn't shift below.
        <Box sx={{ px: 6 }}>
          <Stack spacing={1} sx={{ py: 2 }} data-testid="ad-detail-header-loading">
            <Skeleton variant="text" width={220} height={20} />
            <Skeleton variant="text" width={320} height={40} />
          </Stack>
        </Box>
      ) : (
        <ArtifactDetailHeader
          id="analyticalDatasetDetailPageHeader"
          breadcrumbs={breadcrumbs}
          title={pageTitle}
          status={effectiveStatus || undefined}
          titleTestId="ad-detail-title"
          statusTestId="ad-detail-status"
          // The dataset this AD runs against, as the line under the title — it
          // qualifies the whole artifact rather than sitting in the meta row as
          // one attribute, and opens the same details the project header does.
          subtitle={
            isMetaReady ? (
              <DatasetDetailsLink
                releaseId={datasetReleaseId}
                releaseName={datasetReleaseName}
                testId="ad-detail-dataset"
              />
            ) : undefined
          }
          metaItems={isMetaReady ? metaItems : undefined}
          rightContent={
            // Figma Artifact Header: small (30px) buttons, 16px apart —
            // matches the cohort header's Process button.
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
              <Tooltip
                title={
                  isDownloadInFlight
                    ? formatMessage({ id: 'analyticalDatasetDetail.downloadTooltip.inFlight' })
                    : canDownload
                    ? ''
                    : formatMessage({
                        id: 'analyticalDatasetDetail.downloadTooltip.notProcessed',
                      })
                }
                arrow
              >
                <span>
                  <Button
                    id="ad-detail-download"
                    data-testid="ad-detail-download"
                    variant="outlined"
                    size="small"
                    startIcon={
                      <Icon
                        id="ad-detail-download-icon"
                        icon={isDownloadDisabled ? 'downloadDisabled' : 'download'}
                        width={18}
                        height={18}
                      />
                    }
                    disabled={isDownloadDisabled}
                    onClick={onDownload}
                    sx={{ textTransform: 'none' }}
                  >
                    {formatMessage({ id: 'analyticalDatasetDetail.downloadButton' })}
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title={processTooltip ?? ''} arrow>
                <span>
                  {isInlineProcessEnabled ? (
                    <WaitButton
                      id="ad-detail-process"
                      btnType="primary"
                      size="small"
                      waitButton={isProcessSubmitting}
                      displayLabel={
                        isProcessSubmitting
                          ? formatMessage({ id: 'analyticalDatasetDetail.processButton.queuing' })
                          : formatMessage({ id: 'analyticalDatasetDetail.processButton' })
                      }
                      disabled={isProcessSubmitting || !canProcess}
                      onClickHandler={onProcess}
                      sx={{ textTransform: 'none' }}
                    />
                  ) : (
                    <Button
                      id="ad-detail-process"
                      data-testid="ad-detail-process"
                      variant="contained"
                      size="small"
                      disabled={isProcessModalOpen || !canProcess}
                      onClick={onProcess}
                      sx={{ textTransform: 'none' }}
                    >
                      {formatMessage({ id: 'analyticalDatasetDetail.processButton' })}
                    </Button>
                  )}
                </span>
              </Tooltip>
            </Box>
          }
        />
      )}
      {/* TODO: cleanup with skip_process_confirmation_modal ff */}
      {isInlineProcessEnabled && processError && (
        <Box sx={{ px: 6 }}>
          <Alert severity="error" data-testid="ad-detail-process-error">
            {formatMessage({ id: 'analyticalDatasetDetail.processError' })}
          </Alert>
        </Box>
      )}
      <Box
        sx={{
          px: 6,
          mt: theme.spacing(1),
        }}
      >
        <Tabs
          value={activeTab}
          onChange={onTabChange}
          aria-label={formatMessage({ id: 'analyticalDatasetDetail.tabsAriaLabel' })}
          data-testid="ad-detail-tabs"
          sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}
        >
          {/* First tab uses `pl: 0` + `minWidth: 0` so the active indicator
              sits flush left instead of inset inside MUI's 90px default. */}
          <Tab
            value="definition"
            label={formatMessage({ id: 'analyticalDatasetDetail.tabs.definition' })}
            data-testid="ad-detail-tab-definition"
            sx={{
              pl: 0,
              minWidth: 0,
              typography: 'body2',
              fontWeight: 500,
              letterSpacing: '0.4px',
              color: theme.palette.text.secondary,
              '&.Mui-selected': { color: theme.palette.primary.main },
              '&:hover': { color: theme.palette.text.primary },
            }}
          />
          <Tab
            value="results"
            label={formatMessage({ id: 'analyticalDatasetDetail.tabs.results' })}
            data-testid="ad-detail-tab-results"
            sx={{
              minWidth: 0,
              typography: 'body2',
              fontWeight: 500,
              letterSpacing: '0.4px',
              color: theme.palette.text.secondary,
              '&.Mui-selected': { color: theme.palette.primary.main },
              '&:hover': { color: theme.palette.text.primary },
            }}
          />
        </Tabs>
      </Box>

      {/* Horizontal gutter matches the header/tabs (px: 6) so the section
          cards line up with the DEFINITION tab underline. */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          px: 6,
          py: 3,
          // Results tab: flex column so the grid card fills the remaining
          // viewport and scrolls internally — one scrollbar, not two. The
          // outer `auto` only kicks in below the grid's 400px floor.
          ...(activeTab === 'results' && { display: 'flex', flexDirection: 'column' }),
        }}
      >
        {activeTab === 'definition' && <AnalyticalDatasetDefinitionTab />}
        {activeTab === 'results' && <AnalyticalDatasetResultsTab />}
      </Box>
      <AdStagedChangesPill />
      {!isInlineProcessEnabled && isProcessModalOpen && (
        <AdProcessModal onClose={onCloseProcessModal} />
      )}
      {isDownloadModalOpen && <AdDownloadModal onClose={onCloseDownloadModal} />}
    </Box>
  );
};
